import React, { useEffect } from "react";
import { X } from "lucide-react";
import TestimonialForm from "./TestimonialForm";

/**
 * TestimonialFormModal.
 *
 * Wraps the existing <TestimonialForm> in a centered modal. Closes
 * on backdrop click, Escape, or the explicit X button. Locks body
 * scroll while open so background content can't jitter on iOS.
 * The form itself already handles submit/loading/success/error
 * states and posts to /api/testimonials, where admin moderation
 * holds new submissions until approved.
 */
const TestimonialFormModal = ({ open, onClose }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="testimonial-form-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="testimonial-form-modal-title"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md px-4 sm:px-6 py-10"
      onClick={onClose}
    >
      <div
        className="glass-card max-w-2xl w-full p-8 sm:p-10 relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="testimonial-form-modal-close"
          onClick={onClose}
          aria-label="Close testimonial form"
          className="absolute top-5 right-5 text-themed-muted hover:text-gold transition-colors"
        >
          <X size={20} strokeWidth={1.5} />
        </button>
        <p className="text-[10px] tracking-[0.4em] uppercase text-gold mb-4">
          Share a Reflection
        </p>
        <h2
          id="testimonial-form-modal-title"
          className="font-serif text-3xl sm:text-4xl text-themed leading-tight mb-3"
        >
          Return your{" "}
          <span className="italic gold-gradient-text">reflection.</span>
        </h2>
        <p className="text-sm text-themed-muted mb-8 leading-relaxed">
          Your words may be reviewed before being shared publicly. Thank you
          for adding your voice to the path.
        </p>
        <TestimonialForm />
      </div>
    </div>
  );
};

export default TestimonialFormModal;
